// src/components/ui/lets-work-section.jsx
"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { motion, AnimatePresence, useInView, useScroll, useTransform } from "framer-motion"
import {
  ArrowUpRight,
  Moon,
  Phone,
  Mail,
  Globe,
  Sun,
  Shield,
  Wrench,
  Headphones,
  MapPin,
  Facebook,
  Instagram,
  Linkedin,
} from "lucide-react"

import IconImg from "../Assets/icon.png"
import Cctv from "../Assets/camera.jpg"
import Laptop from "../Assets/laptop.jpg"
import Web from "../Assets/website.jpeg"
import Sup from "../Assets/support.jpg"

// ✅ list motion variants (used in ServiceSection bullets)
const listVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.12,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 10, filter: "blur(8px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
}

export function LetsWorkTogether() {
  const [isHovered, setIsHovered] = useState(false)

  // ✅ modal state
  const [isContactOpen, setIsContactOpen] = useState(false)
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light"
    const stored = window.localStorage.getItem("theme")
    if (stored === "dark" || stored === "light") return stored
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  })

  useEffect(() => {
    const root = document.documentElement
    if (theme === "dark") root.classList.add("dark")
    else root.classList.remove("dark")
    window.localStorage.setItem("theme", theme)
  }, [theme])

  // ✅ Replace with your real business contact
  const CONTACT = useMemo(
    () => ({
      email: "smarttechsolutions.lb@gmail.com",
      phoneDisplay: "+961 70 216 395",
      // ✅ fixed E.164
      phoneE164: "+96170216395",
    }),
    [],
  )

  // ✅ Company location
  const COMPANY_MAPS_URL = "https://maps.app.goo.gl/VQw1JNYLH1FkXmXH9"

  // ✅ Optional: update these if you have them
  const SOCIALS = useMemo(
    () => ({
      facebook: "", // e.g. "https://facebook.com/yourpage"
      instagram: "https://www.instagram.com/stsleb?igsh=dmhubjYxM2p2YWs5", 
      
    }),
    [],
  )

  // ✅ Your local images (correct)
  const IMAGES = useMemo(
    () => ({
      cctv: Cctv,
      web: Web,
      repair: Laptop,
      support: Sup,
    }),
    [],
  )

  // ✅ open modal when clicking the hero / arrow
  const handleHeroClick = (e) => {
    e.preventDefault()
    setIsContactOpen(true)
  }

  const handleCall = () => window.open(`tel:${CONTACT.phoneE164}`, "_self")
  const handleEmail = () => window.open(`mailto:${CONTACT.email}`, "_self")
  const handleLocation = () => window.open(COMPANY_MAPS_URL, "_blank", "noopener,noreferrer")
  const toggleTheme = () => setTheme((prev) => (prev === "dark" ? "light" : "dark"))

  // ✅ Smooth scroll helper for hero shortcuts
  const scrollToId = (id) => {
    const el = document.getElementById(id)
    if (!el) return
    el.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  return (
    <main className="w-full bg-background text-foreground">
      {/* =========================
          HERO
        ========================= */}
      <section className="relative min-h-screen px-6 pt-14 pb-32">
        <div className="absolute right-6 top-6 z-20">
          {/* ✅ Icon-only theme toggle (mobile-friendly) */}
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Light mode" : "Dark mode"}
            className="
              inline-flex items-center justify-center
              size-11 rounded-full border
              bg-background/80 text-foreground
              shadow-sm backdrop-blur
              transition active:scale-[0.98]
              hover:-translate-y-0.5 hover:shadow-md
            "
          >
            {theme === "dark" ? <Sun className="size-5" /> : <Moon className="size-5" />}
          </button>
        </div>

        {/* background VIDEO */}
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <video className="absolute inset-0 h-full w-full object-cover" autoPlay muted loop playsInline preload="metadata">
            <source src="/hero.mp4" type="video/mp4" />
          </video>

          {/* readability overlay */}
          <div className="absolute inset-0 bg-background/75" />
          <div className="absolute inset-0 opacity-60 [background:radial-gradient(1200px_circle_at_50%_20%,hsl(var(--muted))_0%,transparent_60%)]" />
          <div className="absolute inset-x-0 top-0 h-px bg-border/70" />
          <div className="absolute inset-x-0 bottom-0 h-px bg-border/70" />
        </div>

        {/* ✅ layout: brand on top, content in lower third */}
        <div className="relative mx-auto flex min-h-screen w-full max-w-5xl flex-col">
          {/* BRAND HEADING */}
          <div className="group pt-6 pb-8 text-center transition-all duration-500">
            <h1 className="relative inline-flex items-center gap-3 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              <img className="icon" src={IconImg} alt="Smart Tech Solutions" />

              <span className="relative">
                <span className="header bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 bg-[length:200%_200%] bg-clip-text text-transparent transition-all duration-700 group-hover:bg-[position:100%_50%] animate-gradient">
                  Smart Tech Solutions
                </span>

                <span className="absolute -bottom-2 left-1/2 h-[2px] w-3/4 -translate-x-1/2 rounded-full bg-gradient-to-r from-blue-500/0 via-blue-600 to-blue-500/0 opacity-80 blur-[1px]" />
              </span>
            </h1>

            <p className="mt-3 text-xs tracking-[0.35em] uppercase text-muted-foreground">
              Websites • CCTV • Repairs • Online Support
            </p>
          </div>

          {/* ✅ LOWER THIRD CONTENT */}
          <div className="mt-auto flex flex-col items-center gap-16 md:gap-24 pb-24">
            {/* Clickable hero text (opens CONTACT modal) */}
            <div
              className="group relative cursor-pointer select-none"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={handleHeroClick}
            >
              <div className="flex flex-col items-center gap-6">
                <h2 className="relative text-center text-5xl font-light tracking-tight text-foreground sm:text-6xl md:text-7xl lg:text-8xl">
                  <span className="block overflow-hidden">
                    <span
                      className="block transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
                      style={{ transform: isHovered ? "translateY(-8%)" : "translateY(0)" }}
                    >
                      Let’s build
                    </span>
                  </span>
                  <span className="block overflow-hidden">
                    <span
                      className="block transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] delay-75"
                      style={{ transform: isHovered ? "translateY(-8%)" : "translateY(0)" }}
                    >
                      <span className="text-muted-foreground/60">something smart</span>
                    </span>
                  </span>
                </h2>
              </div>
            </div>

            {/* quick actions card (A + C mix) */}
            <div className="w-full max-w-3xl mt-6 md:mt-10">
              <div className="rounded-2xl border bg-background/60 p-6 shadow-[0_10px_40px_rgba(0,0,0,0.04)] backdrop-blur sm:p-8">
                <p className="text-center text-sm leading-relaxed text-muted-foreground sm:text-base">
                  Choose a service to jump right in — details and features below.
                </p>

                {/* A) service shortcuts */}
                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <ActionButton onClick={() => scrollToId("websites")} icon={Globe} label="Websites" />
                  <ActionButton onClick={() => scrollToId("cctv")} icon={Shield} label="CCTV Cameras" />
                  <ActionButton onClick={() => scrollToId("repairs")} icon={Wrench} label="Laptop & PC Repairs" />
                  <ActionButton onClick={() => scrollToId("support")} icon={Headphones} label="Online Support" />
                </div>

                {/* C) offer / promo strip */}
                <div className="mt-6 rounded-2xl border bg-background/60 p-4 text-center backdrop-blur">
                  <p className="text-xs font-semibold tracking-[0.35em] uppercase text-muted-foreground">Business website starter package (1–5 pages) + free domain setup.</p>
                  <p className="mt-2 text-sm text-foreground/90">
                    Tell us what you need — we’ll reply with a clear plan and pricing.
                  </p>

                  <button
                    type="button"
                    onClick={() => setIsContactOpen(true)}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:shadow-[0_15px_40px_rgba(37,99,235,0.35)] active:translate-y-0 sm:w-auto"
                  >
                    Get a Quote <ArrowUpRight className="size-4" />
                  </button>
                </div>

                <div className="pt-4 text-center">
                  <span className="text-xs tracking-widest uppercase text-muted-foreground/60">
                    Scroll to explore our services
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ✅ REMOVED: pinned arrow bottom center (per request) */}
        </div>

        {/* ✅ Contact modal */}
        <AnimatePresence>
          {isContactOpen ? <ContactModal open={isContactOpen} onClose={() => setIsContactOpen(false)} /> : null}
        </AnimatePresence>
      </section>

      {/* =========================
          ABOUT (smooth reveal)
        ========================= */}
      <SectionShell>
        <Reveal>
          <div
            id="about"
            className="rounded-3xl border bg-background/70 p-8 shadow-[0_20px_70px_rgba(0,0,0,0.06)] backdrop-blur sm:p-12"
          >
            <p className="text-xs font-semibold tracking-[0.35em] uppercase text-muted-foreground">About us</p>
            <h2 className="mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
              We build, secure, and fix your tech — end to end.
            </h2>
            <p className="mt-5 max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-base text">
              Smart Tech Solutions specializes in modern responsive websites, CCTV installation & maintenance,
              laptop/PC sales and repairs, and online support for software issues. Clean work, honest pricing,
              and fast response.
            </p>
          </div>
        </Reveal>
      </SectionShell>

      {/* =========================
          SERVICES
        ========================= */}
      <div id="services" />

      <ServiceSection
        id="cctv"
        title="CCTV Cameras"
        desc="Installation, configuration, and maintenance for homes and businesses — optimal placement, clean wiring, and reliable monitoring."
        bullets={[
          "Site assessment",
          "DVR/NVR setup + mobile viewing",
          "2K/4K Cameras",
          "High Quality Night Vision",
          "Light Sensors",
          "Wide Field Of View",
          "Reliable POE Connectivity",
          "Adjustable Focus",
          "Repairs, upgrades, and troubleshooting",
        ]}
        icon={Shield}
        imageUrl={IMAGES.cctv}
      />

      <ServiceSection
        id="websites"
        title="Modern & Responsive Websites"
        desc="Fast, mobile-first websites built to convert — clean UI, smooth interactions, and SEO-friendly structure."
        bullets={[
          "Landing pages & business websites",
          "E-commerce",
          "Responsive design for all devices",
          "Performance + SEO best practices",
        ]}
        icon={Globe}
        imageUrl={IMAGES.web}
        flip
      />

      <ServiceSection
        id="repairs"
        title="Laptop & PC Repairs"
        desc="Hardware and software repairs with clear diagnosis and quick turnaround — upgrades, fixes, and maintenance."
        bullets={[
          "SSD/RAM upgrades and tuning",
          "Fix overheating, boot issues, slowness",
          "Windows reinstall, drivers, backup",
          "Gaming pc installment",
        ]}
        icon={Wrench}
        imageUrl={IMAGES.repair}
      />

      {/* ✅ CHANGE: removed isLast to avoid extra runway/space before footer */}
      <ServiceSection
        id="support"
        title="Online Support & Software Issues"
        desc="Remote support for software issues — installs, troubleshooting, cleanup, and guidance."
        bullets={[
          "Software installation and setup",
          "Virus cleanup + security hardening",
          "Remote troubleshooting & support",
        ]}
        icon={Headphones}
        imageUrl={IMAGES.support}
        flip
      />

      {/* =========================
          FOOTER
        ========================= */}
      <Footer
        contact={CONTACT}
        mapsUrl={COMPANY_MAPS_URL}
        socials={SOCIALS}
        onCall={handleCall}
        onEmail={handleEmail}
        onLocation={handleLocation}
        onOpenContact={() => setIsContactOpen(true)}
      />
    </main>
  )
}

/* =========================
  Helpers (same file)
  ========================= */

function SectionShell({ children }) {
  return (
    <section className="relative mx-auto flex min-h-screen w-full max-w-6xl items-center px-6 py-20">
      {children}
    </section>
  )
}

function Reveal({ children, delay = 0 }) {
  const ref = useRef(null)
  const inView = useInView(ref, { margin: "-10% 0px -10% 0px", once: true })
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 18, filter: "blur(6px)" }}
      animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.div>
  )
}

function ServiceSection({ id, eyebrow, title, desc, bullets, icon: Icon, imageUrl, flip = false, isLast = false }) {
  const wrapRef = useRef(null)

  // Scroll progress for THIS section only
  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start 55%", "end 35%"],
  })

  // Background image pop (smooth)
  const bgOpacity = useTransform(scrollYProgress, [0, 0.18, 0.8, 1], [0, 0.42, 0.42, 0])
  const bgY = useTransform(scrollYProgress, [0, 1], [60, -60])
  const bgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.12, 1.04, 1.1])

  // Cards enter -> sit -> exit (scrubbed)
  const enter = 0.35
  const exit = 0.98

  const textEnterX = flip ? 550 : -580
  const textExitX = flip ? 580 : -580

  const imageEnterX = flip ? -550 : 580
  const imageExitX = flip ? 80 : -80

  const textX = useTransform(scrollYProgress, [0, enter, exit, 1], [textEnterX, 0, 0, textExitX])
  const imageX = useTransform(scrollYProgress, [0, enter, exit, 1], [imageEnterX, 0, 0, imageExitX])

  const cardOpacity = useTransform(
    scrollYProgress,
    [0, 0.12, enter, exit, 0.9, 1],
    [0, 0.15, 1, 1, 0.15, 0],
  )
  const cardScale = useTransform(scrollYProgress, [0, enter, exit, 1], [0.98, 1, 1, 0.98])
  const textFilter = useTransform(scrollYProgress, [0, enter, exit, 1], [
    "blur(10px)",
    "blur(0px)",
    "blur(0px)",
    "blur(10px)",
  ])

  return (
    <section
      id={id}
      ref={wrapRef}
      className={`relative overflow-hidden py-24 ${isLast ? "min-h-[120vh]" : ""}`}
    >
      {/* Background image pop + parallax */}
      <motion.div className="pointer-events-none absolute inset-0" style={{ opacity: bgOpacity }}>
        <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${imageUrl})` }} />
          <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/75 to-background/95" />
          <div className="absolute inset-0 [background:radial-gradient(900px_circle_at_50%_30%,rgba(59,130,246,0.18)_0%,transparent_60%)]" />
        </motion.div>
      </motion.div>

      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-10 px-6 md:grid-cols-2">
        {/* TEXT CARD */}
        <motion.div
          className={`order-2 ${flip ? "md:order-2" : "md:order-1"}`}
          style={{ x: textX, opacity: cardOpacity, scale: cardScale, filter: textFilter }}
        >
          <div className="rounded-3xl border bg-background/70 p-7 shadow-[0_20px_70px_rgba(0,0,0,0.08)] backdrop-blur md:p-10">
            <div className="flex items-center gap-3">
              <span className="inline-flex size-10 items-center justify-center rounded-2xl border bg-background shadow-sm">
                <Icon className="size-5 text-blue-600" strokeWidth={1.8} />
              </span>
              <span className="text-xs font-semibold tracking-[0.35em] uppercase text-muted-foreground">{eyebrow}</span>
            </div>

            <h3 className="mt-5 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{title}</h3>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">{desc}</p>

            {/* ✅ Neon + Framer Motion list (mobile sizing improved) */}
            <motion.ul
              variants={listVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.35 }}
              className="mt-8 space-y-3 sm:space-y-4"
            >
              {bullets.map((b) => (
                <motion.li
                  key={b}
                  variants={itemVariants}
                  className="
                    group flex items-start gap-3 sm:gap-4 rounded-xl
                    border border-border/60
                    bg-background/50 px-3 py-2.5 sm:px-4 sm:py-3
                    backdrop-blur-md
                    shadow-[0_0_0_1px_rgba(255,255,255,0.04)]
                    transition-all duration-300
                    hover:border-cyan-400/40
                    hover:shadow-[0_0_0_1px_rgba(34,211,238,0.20),0_0_26px_rgba(34,211,238,0.12)]
                  "
                >
                  <span className="relative mt-1.5 flex size-2.5 shrink-0">
                    <span className="absolute -inset-2 rounded-full bg-cyan-400/15 blur-md opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400/20 opacity-60" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-cyan-300 shadow-[0_0_18px_rgba(34,211,238,0.60)]" />
                  </span>

                  <span className="text-[13px] leading-snug sm:text-sm sm:leading-relaxed text-foreground/85 transition-colors duration-300 group-hover:text-foreground">
                    {b}
                  </span>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </motion.div>

        {/* IMAGE CARD */}
        <motion.div
          className={`order-1 ${flip ? "md:order-1" : "md:order-2"}`}
          style={{ x: imageX, opacity: cardOpacity, scale: cardScale }}
        >
          <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border bg-muted shadow-[0_25px_80px_rgba(0,0,0,0.10)]">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${imageUrl})` }} />
            <div className="absolute inset-0 bg-gradient-to-tr from-background/85 via-transparent to-transparent" />
          </div>
        </motion.div>
      </div>

      {/* (kept for compatibility if you ever re-enable isLast) */}
      {isLast ? <div className="h-[35vh]" aria-hidden="true" /> : null}
    </section>
  )
}

function ActionButton({ onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className="group inline-flex w-full items-center justify-center gap-2 rounded-xl border bg-background px-4 py-3 text-sm font-semibold text-foreground transition-all hover:-translate-y-0.5 hover:border-foreground/30 hover:shadow-[0_10px_30px_rgba(0,0,0,0.06)] active:translate-y-0"
    >
      <Icon className="size-4 text-muted-foreground transition-colors group-hover:text-foreground" strokeWidth={1.6} />
      <span className="truncate">{label}</span>
      <ArrowUpRight
        className="size-4 opacity-60 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100"
        strokeWidth={1.6}
      />
    </button>
  )
}

/* =========================
  Footer
  ========================= */

function Footer({ contact, mapsUrl, socials, onCall, onEmail, onLocation, onOpenContact }) {
  const year = new Date().getFullYear()

  const open = (url) => {
    if (!url) return
    window.open(url, "_blank", "noopener,noreferrer")
  }

  return (
    <footer className="relative overflow-hidden border-t bg-background">
      {/* glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 left-1/2 h-72 w-[46rem] -translate-x-1/2 rounded-full bg-blue-600/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-56 w-56 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-56 w-56 rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      <div className="relative mx-auto w-full max-w-6xl px-6 py-16">
        {/* Top row: CTA card */}
        <div className="rounded-3xl border bg-background/70 p-6 shadow-[0_20px_70px_rgba(0,0,0,0.06)] backdrop-blur sm:p-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold tracking-[0.35em] uppercase text-muted-foreground">Let’s work together</p>
              <h3 className="mt-2 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Need a website, CCTV, repairs, or support?
              </h3>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">
                Tell us what you need and we’ll get back to you quickly with a clear plan and fair pricing.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:min-w-[260px]">
              <button
                type="button"
                onClick={onOpenContact}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:shadow-[0_18px_50px_rgba(37,99,235,0.35)] active:translate-y-0"
              >
                Open Contact Form <ArrowUpRight className="size-4" />
              </button>

              <div className="grid grid-cols-2 gap-3">
                <FooterMiniButton onClick={onCall} icon={Phone} label="Call" />
                <FooterMiniButton onClick={onLocation} icon={MapPin} label="Maps" />
              </div>
            </div>
          </div>
        </div>

        {/* Links grid */}
        <div className="mt-12 grid grid-cols-1 gap-10 md:grid-cols-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src={IconImg}
                alt="Smart Tech Solutions"
                className="h-10 w-10 rounded-xl border bg-background p-1 shadow-sm"
              />
              <div>
                <p className="text-lg font-bold tracking-tight">Smart Tech Solutions</p>
                <p className="text-xs tracking-[0.30em] uppercase text-muted-foreground">
                  Websites • CCTV • Repairs • Online Support
                </p>
              </div>
            </div>

            <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
              Modern websites, secure installations, and fast, honest technical support. We focus on clean work,
              reliability, and quick response.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <SocialPill icon={Facebook} label="Facebook" onClick={() => open(socials.facebook)} disabled={!socials.facebook} />
              <SocialPill
                icon={Instagram}
                label="Instagram"
                onClick={() => open(socials.instagram)}
                disabled={!socials.instagram}
              />
              
            </div>

          </div>

          {/* Quick Links */}
          <div className="md:col-span-3">
            <p className="text-xs font-semibold tracking-[0.35em] uppercase text-muted-foreground">Quick links</p>
            <ul className="mt-4 space-y-3 text-sm">
              <FooterLink href="#services" label="Services" />
              <FooterLink href="#about" label="About" />
              <li>
                <button
                  type="button"
                  onClick={onOpenContact}
                  className="group inline-flex items-center gap-2 text-foreground/80 transition hover:text-foreground"
                >
                  Contact{" "}
                  <ArrowUpRight className="size-4 opacity-70 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  className="group inline-flex items-center gap-2 text-foreground/80 transition hover:text-foreground"
                >
                  Back to top{" "}
                  <ArrowUpRight className="size-4 opacity-70 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <p className="text-xs font-semibold tracking-[0.35em] uppercase text-muted-foreground">Contact</p>

            <div className="mt-4 space-y-3">
              <ContactRow icon={Phone} title="Phone" value={contact.phoneDisplay} onClick={onCall} />
              <ContactRow icon={Mail} title="Email" value={contact.email} onClick={onEmail} />
              <ContactRow icon={MapPin} title="Location" value="Open in Google Maps" onClick={() => open(mapsUrl)} />
            </div>

            <div className="mt-6 rounded-2xl border bg-background/60 p-4 backdrop-blur">
              <p className="text-sm font-semibold">Business hours</p>
              <p className="mt-1 text-sm text-muted-foreground">Monday to Friday (9AM-6PM)</p>
              <p className="mt-1 text-sm text-muted-foreground">Saturday (9AM-3PM)</p>
              
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 flex flex-col gap-3 border-t pt-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>© {year} Smart Tech Solutions. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-3">
            <span className="opacity-70">Built with care.</span>
            <span className="hidden sm:inline opacity-40">•</span>
          
          </div>
        </div>
      </div>
    </footer>
  )
}

function FooterMiniButton({ onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group inline-flex items-center justify-center gap-2 rounded-2xl border bg-background px-4 py-3 text-sm font-semibold transition hover:-translate-y-0.5 hover:shadow-sm active:translate-y-0"
    >
      <Icon className="size-4 text-muted-foreground transition-colors group-hover:text-foreground" strokeWidth={1.7} />
      {label}
    </button>
  )
}

function SocialPill({ icon: Icon, label, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className="
        group inline-flex items-center gap-2 rounded-full border
        bg-background/70 px-4 py-2 text-xs font-semibold
        text-foreground/80 backdrop-blur
        transition hover:-translate-y-0.5 hover:text-foreground hover:shadow-sm active:translate-y-0
        disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:translate-y-0 disabled:hover:shadow-none
      "
    >
      <Icon className="size-4 text-muted-foreground transition-colors group-hover:text-foreground" strokeWidth={1.7} />
      {label}
      {!disabled ? (
        <ArrowUpRight className="size-4 opacity-55 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
      ) : null}
    </button>
  )
}

function FooterLink({ href, label }) {
  return (
    <li>
      <a className="group inline-flex items-center gap-2 text-foreground/80 transition hover:text-foreground" href={href}>
        {label}
        <ArrowUpRight className="size-4 opacity-55 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
      </a>
    </li>
  )
}

function ContactRow({ icon: Icon, title, value, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="
        group flex w-full items-start gap-3 rounded-2xl border
        bg-background/60 p-4 text-left backdrop-blur
        transition hover:-translate-y-0.5 hover:shadow-[0_14px_40px_rgba(0,0,0,0.08)] active:translate-y-0
      "
    >
      <span className="inline-flex size-10 items-center justify-center rounded-xl border bg-background shadow-sm">
        <Icon className="size-5 text-blue-600" strokeWidth={1.8} />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{title}</span>
        <span className="mt-0.5 block truncate text-sm text-muted-foreground">{value}</span>
      </span>
      <ArrowUpRight className="ml-auto mt-1 size-4 opacity-55 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
    </button>
  )
}

/* =========================
  Contact Modal (same file)
  ========================= */

function ContactModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" })
  const [touched, setTouched] = useState(false)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState("")

  const errors = {
    name: form.name.trim().length < 2,
    email: !/^\S+@\S+\.\S+$/.test(form.email.trim()),
    phone: form.phone.trim().length < 6,
    message: form.message.trim().length < 10,
  }
  const hasError = Object.values(errors).some(Boolean)

  const update = (key) => (e) => {
    setSent(false)
    setError("")
    setForm((p) => ({ ...p, [key]: e.target.value }))
  }

  const resetAll = () => {
    setForm({ name: "", email: "", phone: "", message: "" })
    setTouched(false)
    setSending(false)
    setSent(false)
    setError("")
  }

  const close = () => {
    resetAll()
    onClose()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setTouched(true)
    setSent(false)
    setError("")

    if (hasError) return

    const payload = {
      name: form.name,
      email: form.email,
      message: `Phone: ${form.phone}\n\n${form.message}`,
    }

    try {
      setSending(true)

      const res = await fetch("/api/save_message.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })

      const data = await res.json()

      if (!data.success) throw new Error("API returned success=false")

      setSent(true)
      setSending(false)

      setTimeout(() => {
        close()
      }, 1200)
    } catch (err) {
      setSending(false)
      setError("Something went wrong. Please try again.")
    }
  }

  if (!open) return null

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close()
      }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <motion.div
        className="relative w-full max-w-xl overflow-hidden rounded-3xl border bg-background shadow-[0_30px_120px_rgba(0,0,0,0.35)]"
        initial={{ y: 18, scale: 0.98, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: 18, scale: 0.98, opacity: 0 }}
        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="flex items-center justify-between border-b px-6 py-5">
          <div>
            <h3 className="mt-1 text-xl font-bold tracking-tight text-foreground">Contact Smart Tech Solutions</h3>
            <p className="mt-1 text-xs tracking-wide text-muted-foreground">Fill this form and we’ll get back to you soon.</p>
          </div>

          <button
            onClick={close}
            className="rounded-xl border bg-background px-3 py-2 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:shadow-sm active:translate-y-0"
            type="button"
          >
            Close
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-6">
          {sent ? (
            <div className="mb-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-foreground">
              ✅ Message sent — we’ll reply soon.
            </div>
          ) : null}

          {error ? (
            <div className="mb-4 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-foreground">
              ❌ {error}
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Full name"
              value={form.name}
              onChange={update("name")}
              placeholder="Your name"
              error={touched && errors.name ? "Please enter at least 2 characters." : ""}
              disabled={sending || sent}
            />
            <Field
              label="Email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@example.com"
              error={touched && errors.email ? "Please enter a valid email." : ""}
              inputMode="email"
              disabled={sending || sent}
            />

            <div className="sm:col-span-2">
              <Field
                label="Phone number"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+961 ..."
                error={touched && errors.phone ? "Please enter a valid phone number." : ""}
                inputMode="tel"
                disabled={sending || sent}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="mb-2 block text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground">
                Message
              </label>
              <textarea
                value={form.message}
                onChange={update("message")}
                placeholder="Tell us what you need (website, CCTV, repair, software help...)"
                disabled={sending || sent}
                className="min-h-[120px] w-full rounded-2xl border bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-blue-600/50 focus:ring-4 focus:ring-blue-600/10 disabled:opacity-70"
              />
              {touched && errors.message ? <p className="mt-2 text-xs text-red-500">Please enter at least 10 characters.</p> : null}
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">We usually reply fast during business hours.</p>

            <button
              type="submit"
              disabled={sending || sent}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:shadow-[0_15px_40px_rgba(37,99,235,0.35)] disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {sent ? "Sent!" : sending ? "Sending..." : "Send"}
              <ArrowUpRight className="size-4" />
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}

function Field({ label, error, ...props }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground">{label}</label>
      <input
        {...props}
        className="w-full rounded-2xl border bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-blue-600/50 focus:ring-4 focus:ring-blue-600/10"
      />
      {error ? <p className="mt-2 text-xs text-red-500">{error}</p> : null}
    </div>
  )
}
